/**
 * Copy a string to the clipboard.
 *
 * Tries the modern async Clipboard API first (works in HTTPS / focused tabs /
 * most modern setups). Falls back to a hidden-textarea + document.execCommand
 * for older browsers and some iframe contexts where the async API is blocked.
 *
 * Returns true on success.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  // Modern path
  if (
    typeof navigator !== 'undefined' &&
    navigator.clipboard &&
    typeof navigator.clipboard.writeText === 'function'
  ) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Clipboard API can fail in iframes without user activation, or when
      // the document is not focused. Fall through to textarea fallback.
    }
  }

  // Fallback path: temporary textarea + execCommand('copy')
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    // Keep it off-screen but selectable
    ta.style.position = 'fixed';
    ta.style.top = '-9999px';
    ta.style.left = '-9999px';
    ta.setAttribute('readonly', '');
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
